// ═══════════════════════════════════════════════════════════
// useSupaTable — قراءة/إضافة/تعديل/حذف سجلات موديول من Supabase
// أي فشل بيظهر للمستخدم برسالة (notifyError) — مفيش حفظ صامت في الـ state
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import { supaGet, supaGetAll, supaInsert, supaUpdate } from "./supabaseClient";
import { toRow, fromRow, TABLES, hasSoftDelete } from "./dbSchema";

export { toRow, fromRow };

// ───── إشعار خطأ عام — بيعرضه <GlobalErrorToast /> في App ─────
export const notifyError = (msg) => {
  console.error("[ERP]", msg);
  window.dispatchEvent(new CustomEvent("erp:error", { detail: String(msg) }));
};

// ───── سجل التدقيق: كل إضافة/تعديل/حذف بتتسجل في audit_log (فشله ما يوقفش العملية) ─────
const AUDIT_NAMES = { create: "إنشاء", update: "تعديل", delete: "حذف" };
const describe = (r) => (r && (r.name || r.title || r.arabicName || r.member || r.donorName || r.description || r.criterion || r.item || r.employee || r.code)) || "";
export const logAudit = (action, table, record) => {
  if (table === "audit_log") return;
  let user = null;
  try { user = JSON.parse(localStorage.getItem("cnr_user")); } catch { /* */ }
  const label = TABLES[table]?.label || table;
  supaInsert("audit_log", {
    user_id: user?.id ?? null,
    username: user?.username || user?.name || null,
    action,
    module: label.split(" — ")[0].slice(0, 50),
    details: `${AUDIT_NAMES[action] || action}: ${label}${describe(record) ? ` — ${describe(record)}` : ""}`,
  }, { minimal: true }).catch((err) => console.warn("audit log failed:", err.message));
};

// أي تعديل على جدول يُبلغ باقي الشاشات المفتوحة على نفس الجدول لتعيد التحميل (BUG-056)
const announceChange = (table, source) =>
  window.dispatchEvent(new CustomEvent("erp:table-changed", { detail: { table, source } }));

const scopeQuery = (table, scope) =>
  Object.entries(scope).map(([k, v]) => `${k}=eq.${encodeURIComponent(v)}`).join("&");

// ═══════════════════════════════════════════════════════════
// skip = true → لا تحميل (للوحة التحكم: كل دور يحمّل جداوله بس — BUG-021)
export function useSupaTable(table, { scope = {}, order = "id.desc", limit, query = "", select = "*", skip = false } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(!skip);
  const [loadError, setLoadError] = useState("");
  const scopeKey = JSON.stringify(scope);

  const reload = useCallback(async () => {
    if (skip) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const sc = JSON.parse(scopeKey);
      // السجلات المحذوفة حذفاً ناعماً لا تظهر (BUG-048)
      const q = [`select=${select}`, scopeQuery(table, sc), hasSoftDelete(table) ? "deleted_at=is.null" : "", query,
        `order=${order}`, limit ? `limit=${limit}` : ""].filter(Boolean).join("&");
      // بدون limit: كل الصفوف على صفحات (مش أول 1000 بس — BUG-020)
      const data = limit ? await supaGet(table, q) : await supaGetAll(table, q);
      setRows(data.map((r) => fromRow(table, r)));
      setLoadError("");
    } catch (err) {
      setLoadError(err.message);
      notifyError(err.message);
    } finally {
      setLoading(false);
    }
  }, [table, scopeKey, order, limit, query, select, skip]);

  useEffect(() => { reload(); }, [reload]);

  const instanceId = useRef(Math.random().toString(36).slice(2));
  useEffect(() => {
    if (skip) return;
    const onChange = (e) => { if (e.detail?.table === table && e.detail?.source !== instanceId.current) reload(); };
    window.addEventListener("erp:table-changed", onChange);
    return () => window.removeEventListener("erp:table-changed", onChange);
  }, [table, reload, skip]);

  // كل عملية بترجع النتيجة، أو null لو فشلت (والخطأ بيتعرض للمستخدم)
  const run = async (fn) => {
    try { return await fn(); } catch (err) { notifyError(err.message); return null; }
  };

  const insertMany = (objs) => run(async () => {
    if (!objs.length) return [];
    const sc = JSON.parse(scopeKey);
    const saved = await supaInsert(table, objs.map((o) => toRow(table, o, sc)));
    const mapped = saved.map((r) => fromRow(table, r));
    setRows((prev) => [...mapped, ...prev]);
    announceChange(table, instanceId.current);
    mapped.length > 3
      ? logAudit("create", table, { name: `${mapped.length} سجل (استيراد)` })
      : mapped.forEach((m) => logAudit("create", table, m));
    return mapped;
  });

  const insert = async (obj) => {
    const res = await insertMany([obj]);
    return res ? res[0] : null;
  };

  const update = (id, patch) => run(async () => {
    const [saved] = await supaUpdate(table, `id=eq.${id}`, toRow(table, patch));
    const mapped = fromRow(table, saved);
    setRows((prev) => prev.map((r) => (r.id === id ? mapped : r)));
    announceChange(table, instanceId.current);
    logAudit("update", table, mapped);
    return mapped;
  });

  // حذف ناعم: السجل يُعلَّم بـ deleted_at ويختفي من الشاشات، ويبقى في القاعدة للاسترجاع والتدقيق (BUG-048).
  // الحذف الفعلي للإدارة العليا من القاعدة فقط (policy mod_delete)
  const remove = (id) => run(async () => {
    if (!hasSoftDelete(table)) throw new Error(`الحذف غير متاح من هذه الشاشة (${TABLES[table]?.label || table})`);
    const [deleted] = await supaUpdate(table, `id=eq.${id}`, { deleted_at: new Date().toISOString() });
    logAudit("delete", table, deleted && fromRow(table, deleted));
    setRows((prev) => prev.filter((r) => r.id !== id));
    announceChange(table, instanceId.current);
    return true;
  });

  return { rows, loading, loadError, reload, insert, insertMany, update, remove };
}

export default useSupaTable;

// اسم المستخدم الحالي (لحقول "اعتمده" وما شابه)
export const currentUserName = () => {
  try { return JSON.parse(localStorage.getItem("cnr_user"))?.name || ""; } catch { return ""; }
};

// دور المستخدم الحالي (لإخفاء أزرار الحذف/الاعتماد داخل الموديولات)
export const currentRole = () => {
  try { return JSON.parse(localStorage.getItem("cnr_user"))?.role || null; } catch { return null; }
};
// أدوار لا تملك الحذف (BUG-007) — نفس قاعدة سياسة mod_update في supabase/migrations/004
export const NO_DELETE_ROLES = ["preacher", "data_entry"];
export const canDelete = () => !NO_DELETE_ROLES.includes(currentRole());

// الأعداد والنسب من جدول app_settings (BUG-050) — values = { setting_key: value }
export const useAppSettings = (opts = {}) => {
  const api = useSupaTable("app_settings", { order: "id.asc", ...opts });
  const values = Object.fromEntries(api.rows.map((r) => [r.key, r.value]));
  return { ...api, values };
};
